/**
 * Nakładka per-tenant dla layoutu SYSTEMOWEGO (is_system_managed).
 *
 * Kontekst: na layoucie systemowym tenant może UKRYWAĆ (i tylko dla zakładek —
 * przestawiać kolejność), ale zawartość i struktura są nietykalne. Tego stanu NIE
 * wolno zapisywać do `layout_config` systemowego layoutu — jest on re-seedowalny
 * (self-heal nadpisuje `layout_config` gdy layout jest „pristine"). Dlatego ukrycia
 * żyją w osobnej kolumnie-nakładce (`tab_overrides`), a TU jest jedno miejsce, które
 * składa nakładkę z surowym `layout_config` przy ODCZYCIE (karta, builder) oraz
 * wyprowadza nakładkę ze stanu buildera przy ZAPISIE.
 *
 * Zakres nakładki (zasada: „systemowy = TYLKO ukrywanie, żadnego przesuwania"):
 *  - **Zakładki** — `hidden` (slugi ukrytych) + `order` (slugi w kolejności).
 *    Klucz = `slug` (stabilny; `id` zakładki bywa per-layout).
 *  - **Zwykłe pola** — `hiddenFieldIds` (fieldId ukrytych pól). Klucz = `fieldId`
 *    (stabilny — upsert po slugu w katalogu, nie rotuje przy re-seedzie).
 *  - **Formatki** (adresy/konta/kontakty/adres macierzysty) — `elementOverrides`
 *    keyed po **TYPIE elementu** (np. `contractor_addresses_view`), NIE po `element.id`.
 *    `element.id` jest generowany `crypto.randomUUID()` przy każdym seedzie
 *    (`buildDefaultLayoutConfig`) → rotuje przy re-seedzie i osierociłby nakładkę.
 *    Typ formatki jest unikalny w obrębie systemowego layoutu i stabilny, więc jest
 *    poprawnym kluczem (ta sama lekcja co zakładki→slug). Wartość = patch ukryć
 *    scalany do `element.settings` (`hiddenColumnIds` / `hiddenFields`) — read-side
 *    formatek już czyta te klucze z `element.settings`, więc scalenie na odczycie
 *    wystarcza bez zmian w rendererach.
 *  - **Tabele dodatkowe** (`additional_view` i starszy alias `satellite_view`) — też w
 *    `elementOverrides`, ale keyed po **TABELI**: `additional_table:<tableSlug>`. Typ nie
 *    wystarcza, bo układ może mieć kilka tabel dodatkowych (w sekcjach i jako całe
 *    zakładki); slug tabeli jest stabilny przy re-seedzie. Element bez `tableSlug` nie
 *    dostaje ani nie oddaje nakładki. Wartość = ten sam patch ukryć (`hiddenColumnIds`),
 *    karta czyta maskę z `element.settings` (`AdditionalTableTab`).
 *  - **Widoki SQL** (`additional_query_view`) — też w `elementOverrides`, keyed po **WIDOKU**:
 *    `additional_query:<viewSlug>` (ten sam powód co tabele: kilka widoków na jednym układzie).
 *    Element bez `viewSlug` nie dostaje ani nie oddaje nakładki. Wartość = patch ukryć
 *    (`hiddenColumnIds`; id kolumn = `additionalViewColumnIds`).
 *
 * Renderer karty (`RecursiveFormRenderer`) honoruje `tab.hidden`/`tab.sortOrder`
 * (zakładki) oraz — po tej zmianie — `element.hidden` na polach (pomija ukryte).
 * Formatki honorują scalone `element.settings`. Dzięki temu nakładanie tej warstwy
 * na config na odczycie nie wymaga dalszych zmian w rendererach.
 */

/** Klucze ustawień formatek, które wolno trzymać w nakładce (tylko ukrycia — bez kolejności). */
const FORMATKA_OVERLAY_KEYS = ['hiddenColumnIds', 'hiddenFields'] as const;

/** Typy elementu tabeli dodatkowej (`satellite_view` = starszy alias `additional_view`). */
const ADDITIONAL_TABLE_ELEMENT_TYPES: ReadonlySet<string> = new Set([
  'additional_view',
  'satellite_view',
]);

/** Typ elementu widoku SQL (`settings.viewSlug`) — klucz nakładki `additional_query:<viewSlug>`. */
const ADDITIONAL_QUERY_ELEMENT_TYPE = 'additional_query_view';

export interface TabOverrides {
  /** Slugi zakładek ukrytych przez tenanta. */
  hidden: string[];
  /** Slugi zakładek w docelowej kolejności (zakładki spoza listy zostają po nich, w oryginalnej kolejności). */
  order: string[];
  /** fieldId ukrytych zwykłych pól (klucz = fieldId, stabilny przy re-seedzie). */
  hiddenFieldIds?: string[];
  /**
   * Nakładka ukryć formatek, keyed po TYPIE elementu (tabela dodatkowa:
   * `additional_table:<tableSlug>`) → patch scalany do `element.settings`.
   */
  elementOverrides?: Record<string, Record<string, unknown>>;
}

/** Czy nakładka niesie jakąkolwiek zmianę (pusta/`null` = brak). */
export function hasTabOverrides(
  overrides: TabOverrides | null | undefined,
): overrides is TabOverrides {
  if (!overrides) return false;
  return (
    (Array.isArray(overrides.hidden) && overrides.hidden.length > 0) ||
    (Array.isArray(overrides.order) && overrides.order.length > 0) ||
    (Array.isArray(overrides.hiddenFieldIds) && overrides.hiddenFieldIds.length > 0) ||
    (!!overrides.elementOverrides && Object.keys(overrides.elementOverrides).length > 0)
  );
}

/** Luźny kształt elementu layoutu — tyle ile potrzeba, żeby przejść drzewo i nałożyć nakładkę. */
interface LooseElement {
  type?: string;
  fieldId?: string;
  hidden?: boolean;
  settings?: Record<string, unknown>;
  children?: unknown[];
  tabs?: Array<{
    slug?: string | null;
    hidden?: boolean;
    sortOrder?: number;
    children?: unknown[];
  }>;
  accordionItems?: Array<{ children?: unknown[] }>;
  steps?: Array<{ children?: unknown[] }>;
}

/**
 * Klucz elementu w `elementOverrides` — jedno miejsce dla odczytu i zapisu nakładki (także
 * dla kreatora, który rozkłada ukrycia na kopie elementu: `applyOverlayPatchByKey`).
 * Tabela dodatkowa: `additional_table:<tableSlug>` (wspólny dla `additional_view` i
 * `satellite_view`); bez niepustego `tableSlug` — `undefined` (brak wpisu i brak scalenia).
 * Widok SQL (`additional_query_view`): `additional_query:<viewSlug>`; bez niepustego `viewSlug` —
 * `undefined` (dwa widoki SQL na jednym układzie systemowym nie dzielą maski ukryć).
 * Pozostałe typy: sam typ elementu.
 */
export function elementOverrideKey(el: {
  type?: string;
  settings?: Record<string, unknown>;
}): string | undefined {
  if (!el.type) return undefined;
  if (ADDITIONAL_TABLE_ELEMENT_TYPES.has(el.type)) {
    const tableSlug = el.settings?.tableSlug;
    return typeof tableSlug === 'string' && tableSlug !== ''
      ? `additional_table:${tableSlug}`
      : undefined;
  }
  if (el.type === ADDITIONAL_QUERY_ELEMENT_TYPE) {
    const viewSlug = el.settings?.viewSlug;
    return typeof viewSlug === 'string' && viewSlug !== ''
      ? `additional_query:${viewSlug}`
      : undefined;
  }
  return el.type;
}

/** Czy łatka `settings` niesie WYŁĄCZNIE klucze ukryć nakładki (`hiddenColumnIds` / `hiddenFields`). */
export function isOverlayMaskPatch(patch: Record<string, unknown>): boolean {
  const keys = Object.keys(patch);
  return (
    keys.length > 0 &&
    keys.every((key) => (FORMATKA_OVERLAY_KEYS as readonly string[]).includes(key))
  );
}

/**
 * Scala łatkę ukryć do `settings` KAŻDEGO elementu drzewa o kluczu nakładki `key`
 * (`elementOverrideKey`), w dowolnym zagnieżdżeniu. Zwraca NOWĄ tablicę (immutable).
 *
 * Po co: nakładka ma JEDEN wpis na klucz, a odczyt nakłada go na wszystkie elementy z tym
 * kluczem. Gdy na układzie systemowym ta sama tabela dodatkowa stoi kilka razy (np. klocek w
 * sekcji i cała zakładka), kreator musi trzymać w każdej kopii tę samą maskę — inaczej zapis
 * (`deriveSystemOverrides`) wziąłby maskę ostatniej kopii w drzewie. Pusta maska w łatce czyści
 * ją więc we wszystkich kopiach. Pola (`type: 'field'`) są pomijane jak w odczycie i zapisie
 * nakładki; każda kopia dostaje własną kopię tablic z łatki.
 */
export function applyOverlayPatchByKey<
  T extends { type?: string; settings?: Record<string, unknown> },
>(elements: readonly T[], key: string, patch: Record<string, unknown>): T[] {
  const transform = (elRaw: unknown): unknown => {
    if (!elRaw || typeof elRaw !== 'object') return elRaw;
    let next = elRaw as LooseElement;
    if (next.type === 'field') return elRaw;
    if (elementOverrideKey(next) === key) {
      const ownPatch = Object.fromEntries(
        Object.entries(patch).map(([k, v]) => [k, Array.isArray(v) ? [...v] : v]),
      );
      next = { ...next, settings: { ...(next.settings ?? {}), ...ownPatch } };
    }
    if (Array.isArray(next.children)) next = { ...next, children: next.children.map(transform) };
    if (Array.isArray(next.tabs)) {
      next = {
        ...next,
        tabs: next.tabs.map((tab) =>
          Array.isArray(tab.children) ? { ...tab, children: tab.children.map(transform) } : tab,
        ),
      };
    }
    if (Array.isArray(next.accordionItems)) {
      next = {
        ...next,
        accordionItems: next.accordionItems.map((it) =>
          Array.isArray(it.children) ? { ...it, children: it.children.map(transform) } : it,
        ),
      };
    }
    if (Array.isArray(next.steps)) {
      next = {
        ...next,
        steps: next.steps.map((st) =>
          Array.isArray(st.children) ? { ...st, children: st.children.map(transform) } : st,
        ),
      };
    }
    return next;
  };
  return elements.map(transform) as T[];
}

/**
 * Nakłada `tab_overrides` na surowy `layout_config`. Zwraca NOWY obiekt (immutable) —
 * gdy brak nakładki, zwraca ten sam `config` (stabilność referencji dla memo).
 * Rekurencyjnie przechodzi całe drzewo: zakładki (hidden/order), pola (hidden flag),
 * formatki i tabele dodatkowe (scalenie ukryć do settings). Generyczne po typie configu — zachowuje typ
 * wejścia (RFR `LayoutConfig` lub lokalny buildera).
 */
export function applyTabOverrides<T extends object>(
  config: T,
  overrides: TabOverrides | null | undefined,
): T {
  const cfg = config as { elements?: unknown[] };
  if (!config || !Array.isArray(cfg.elements)) return config;
  if (!hasTabOverrides(overrides)) return config;

  const hiddenTabSet = new Set(overrides.hidden ?? []);
  const rank = new Map<string, number>();
  (overrides.order ?? []).forEach((slug, i) => {
    if (!rank.has(slug)) rank.set(slug, i);
  });
  const ORDER_FALLBACK_BASE = (overrides.order ?? []).length;
  const hiddenFieldSet = new Set(overrides.hiddenFieldIds ?? []);
  const elementOverrides = overrides.elementOverrides ?? {};

  const transform = (elRaw: unknown): unknown => {
    const el = elRaw as LooseElement;

    // Pole zwykłe — ukrycie przez fieldId (flaga honorowana przez renderer karty).
    if (el.type === 'field') {
      if (el.fieldId != null && hiddenFieldSet.has(el.fieldId)) {
        return { ...el, hidden: true };
      }
      return elRaw;
    }

    let next: LooseElement = el;

    // Formatka / tabela dodatkowa — scal patch ukryć (klucz: `elementOverrideKey`) do `element.settings`.
    const overrideKey = elementOverrideKey(el);
    const overridePatch = overrideKey !== undefined ? elementOverrides[overrideKey] : undefined;
    if (overridePatch) {
      next = { ...next, settings: { ...(next.settings ?? {}), ...overridePatch } };
    }

    // Zakładki — ukrycie + kolejność per slug; oraz rekurencja w children zakładek.
    if (el.type === 'tabs' && Array.isArray(el.tabs)) {
      const tabs = el.tabs.map((tab, idx) => {
        const t = { ...tab };
        if (tab.slug != null && hiddenTabSet.has(tab.slug)) t.hidden = true;
        const r = tab.slug != null ? rank.get(tab.slug) : undefined;
        t.sortOrder = r !== undefined ? r : ORDER_FALLBACK_BASE + idx;
        if (Array.isArray(tab.children)) t.children = tab.children.map(transform);
        return t;
      });
      next = { ...next, tabs };
    }

    // Rekurencja w pozostałe worki dzieci.
    if (Array.isArray(next.children)) {
      next = { ...next, children: next.children.map(transform) };
    }
    if (Array.isArray(next.accordionItems)) {
      next = {
        ...next,
        accordionItems: next.accordionItems.map((it) => ({
          ...it,
          children: Array.isArray(it.children) ? it.children.map(transform) : it.children,
        })),
      };
    }
    if (Array.isArray(next.steps)) {
      next = {
        ...next,
        steps: next.steps.map((st) => ({
          ...st,
          children: Array.isArray(st.children) ? st.children.map(transform) : st.children,
        })),
      };
    }

    return next === el ? elRaw : next;
  };

  return { ...config, elements: cfg.elements.map(transform) } as T;
}

/**
 * Wyprowadza nakładkę ze stanu zakładek buildera (płaska lista `LayoutTab`-podobna).
 * `hidden` = slugi ukrytych; `order` = slugi wg bieżącego `sortOrder` (a przy równości —
 * kolejności wejściowej). Zakładki bez `slug` są pomijane (nakładka klucza po slugu).
 */
export function deriveTabOverrides(
  tabs: { slug?: string | null; hidden?: boolean; sortOrder?: number }[],
): TabOverrides {
  const withSlug = tabs
    .map((t, idx) => ({ slug: t.slug ?? null, hidden: !!t.hidden, sortOrder: t.sortOrder, idx }))
    .filter((t) => t.slug != null);

  const ordered = [...withSlug].sort((a, b) => {
    const ao = a.sortOrder ?? Number.POSITIVE_INFINITY;
    const bo = b.sortOrder ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return a.idx - b.idx;
  });

  return {
    hidden: ordered.filter((t) => t.hidden).map((t) => t.slug as string),
    order: ordered.map((t) => t.slug as string),
  };
}

/**
 * Wyprowadza PEŁNĄ nakładkę systemowego layoutu z drzewa `elements` buildera (stan
 * tree-native, ten sam który poszedłby do zapisu jako `layout_config`). Zbiera:
 *  - zakładki (hidden/order) z pierwszego elementu `type:'tabs'`,
 *  - `hiddenFieldIds` — fieldId pól z flagą `hidden` (dowolnie zagnieżdżonych),
 *  - `elementOverrides` — ukrycia formatek keyed po TYPIE elementu, a tabel dodatkowych po
 *    `additional_table:<tableSlug>` (`elementOverrideKey`; tylko klucze
 *    `hiddenColumnIds`/`hiddenFields` z niepustą wartością; kolejność świadomie
 *    pominięta — na systemowym reorder jest zablokowany).
 */
export function deriveSystemOverrides(elements: unknown[]): TabOverrides {
  const tabsEl = (elements as LooseElement[]).find((el) => el?.type === 'tabs');
  const base = deriveTabOverrides(
    (tabsEl?.tabs ?? []).map((t) => ({ slug: t.slug, hidden: t.hidden, sortOrder: t.sortOrder })),
  );

  const hiddenFieldIds: string[] = [];
  const elementOverrides: Record<string, Record<string, unknown>> = {};

  const walk = (elRaw: unknown): void => {
    const el = elRaw as LooseElement;
    if (!el || typeof el !== 'object') return;

    if (el.type === 'field') {
      if (el.hidden === true && el.fieldId != null && !hiddenFieldIds.includes(el.fieldId)) {
        hiddenFieldIds.push(el.fieldId);
      }
      return;
    }

    // Formatka / tabela dodatkowa: wyciągnij tylko klucze ukryć z niepustą wartością.
    const overrideKey = elementOverrideKey(el);
    if (overrideKey !== undefined && el.settings) {
      const patch: Record<string, unknown> = {};
      for (const key of FORMATKA_OVERLAY_KEYS) {
        const v = el.settings[key];
        if (Array.isArray(v) && v.length > 0) patch[key] = v;
      }
      if (Object.keys(patch).length > 0) elementOverrides[overrideKey] = patch;
    }

    if (Array.isArray(el.children)) el.children.forEach(walk);
    if (Array.isArray(el.tabs)) el.tabs.forEach((t) => (t.children ?? []).forEach(walk));
    if (Array.isArray(el.accordionItems))
      el.accordionItems.forEach((it) => (it.children ?? []).forEach(walk));
    if (Array.isArray(el.steps)) el.steps.forEach((st) => (st.children ?? []).forEach(walk));
  };

  elements.forEach(walk);

  return {
    ...base,
    ...(hiddenFieldIds.length > 0 ? { hiddenFieldIds } : {}),
    ...(Object.keys(elementOverrides).length > 0 ? { elementOverrides } : {}),
  };
}
